import { IGridComponent } from "@faire/web-api/indigofair/data/IGridComponent";
import { IImageTile } from "@faire/web-api/indigofair/data/IImageTile";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";

import { CIRCLE_TILE_WIDTH_PIXELS } from "@faire/retailer/components/SDUI/ComponentRenderers/Tile/sharedStylingConsts";

import * as v2GridStyles from "./v2Styles.css";

const getMaxNumRows = (
  truncationBehaviour: IGridComponent.ITruncationBehaviour | undefined,
  isExpanded: boolean
) => {
  if (
    (isExpanded && truncationBehaviour?.expand) ||
    truncationBehaviour?.none
  ) {
    // show all rows
    return `repeat(auto-fill, minmax(0px, 1fr))`;
  }
  const definedMaxRows =
    truncationBehaviour?.clip?.max_rows ??
    truncationBehaviour?.expand?.collapsed_max_rows;
  if (definedMaxRows) {
    // show a certain number of rows until expanded (if allowed)
    return `repeat(${definedMaxRows}, 1fr)`;
  }

  return undefined;
};

const getAutoRows = (
  truncationBehaviour: IGridComponent.ITruncationBehaviour | undefined,
  isExpanded: boolean
) => {
  if (isExpanded && truncationBehaviour?.expand) {
    return "auto"; // allow number of rows to grow to fit content
  }
  return "0px"; // hide all rows not defined by grid-template-columns
};

const getColumnWidth = (
  imageTileVariant: keyof typeof IImageTile.ImageTileVariant
) => {
  if (
    imageTileVariant === IImageTile.ImageTileVariant.CIRCLE_TILE ||
    imageTileVariant === IImageTile.ImageTileVariant.SMALL_CIRCLE_TILE
  ) {
    return CIRCLE_TILE_WIDTH_PIXELS;
  }

  return {
    mobileAndAbove: 0,
  };
};

// intended to be used with v2GridStyles.gridAutoLayout
export const getGridAutoLayoutRowResponsiveStyleVars = (
  truncationBehaviour: ResponsiveValues<IGridComponent.ITruncationBehaviour>,
  isExpanded: boolean,
  imageTileVariant: keyof typeof IImageTile.ImageTileVariant
) => {
  const mobileAndAboveTruncationBehaviour = truncationBehaviour.mobileAndAbove;
  const tabletAndAboveTruncationBehaviour =
    truncationBehaviour.tabletAndAbove ?? mobileAndAboveTruncationBehaviour;
  const desktopAndAboveTruncationBehaviour =
    truncationBehaviour.desktopAndAbove ?? tabletAndAboveTruncationBehaviour;

  const responsiveTileWidths = getColumnWidth(imageTileVariant);
  const mobileWidth = responsiveTileWidths.mobileAndAbove;
  const tabletWidth = responsiveTileWidths.tabletAndAbove ?? mobileWidth;
  const desktopWidth = responsiveTileWidths.desktopAndAbove ?? tabletWidth;

  return {
    [v2GridStyles.varMaxGridRowsMobileAndAbove]: getMaxNumRows(
      mobileAndAboveTruncationBehaviour,
      isExpanded
    ),
    [v2GridStyles.varGridAutoRowsMobileAndAbove]: getAutoRows(
      mobileAndAboveTruncationBehaviour,
      isExpanded
    ),
    [v2GridStyles.varMaxGridRowsTabletAndAbove]: getMaxNumRows(
      tabletAndAboveTruncationBehaviour,
      isExpanded
    ),
    [v2GridStyles.varGridAutoRowsTabletAndAbove]: getAutoRows(
      tabletAndAboveTruncationBehaviour,
      isExpanded
    ),
    [v2GridStyles.varMaxGridRowsDesktopAndAbove]: getMaxNumRows(
      desktopAndAboveTruncationBehaviour,
      isExpanded
    ),
    [v2GridStyles.varGridAutoRowsDesktopAndAbove]: getAutoRows(
      desktopAndAboveTruncationBehaviour,
      isExpanded
    ),
    [v2GridStyles.varColumnWidthMobile]: `${mobileWidth}px`,
    [v2GridStyles.varColumnWidthTablet]: `${tabletWidth}px`,
    [v2GridStyles.varColumnWidthDesktop]: `${desktopWidth}px`,
  };
};

const getExpandButtonVisibility = (
  truncationBehaviour: IGridComponent.ITruncationBehaviour | undefined,
  isExpanded: boolean
) => {
  return !truncationBehaviour?.expand || isExpanded ? "hidden" : "visible";
};

export const getGridExpandButtonResponsiveStyleVars = (
  truncationBehaviour: ResponsiveValues<IGridComponent.ITruncationBehaviour>,
  isExpanded: boolean
) => {
  return {
    [v2GridStyles.varExpandButtonVisibilityMobileAndAbove]:
      getExpandButtonVisibility(truncationBehaviour.mobileAndAbove, isExpanded),
    [v2GridStyles.varExpandButtonVisibilityTabletAndAbove]:
      getExpandButtonVisibility(truncationBehaviour.tabletAndAbove, isExpanded),
    [v2GridStyles.varExpandButtonVisibilityDesktopAndAbove]:
      getExpandButtonVisibility(
        truncationBehaviour.desktopAndAbove,
        isExpanded
      ),
  };
};
